import type { Candidate, ProfileFacts, Signal, TrustTier } from './types'
import { CORROBORATING_SIGNAL_IDS, PAUSED_SIGNAL_IDS } from './constants'
import { locationConsistencyCheck } from './locationConsistency'

// Hiring checkpoints a recruiter can re-confirm a Live Vouch at (§12.1). The
// wire value is free-text — these are just the suggested labels, NOT an ATS
// funnel. Re-confirming the same human across stages is the continuity signal a
// candidate-swap farm can't fake cheaply.
export const CHECKPOINTS = [
  { value: 'screen', label: 'Screen' },
  { value: 'pre_technical', label: 'Before technical' },
  { value: 'offer', label: 'At offer' },
] as const

export function checkpointLabel(value?: string): string {
  if (!value) return 'Ad-hoc'
  return CHECKPOINTS.find((c) => c.value === value)?.label ?? value
}

/** Distinct confirming devices across the Live Vouch history (§12.2). More than
 *  one is a recruiter-side *review* flag — never a block (multi-device is
 *  legitimate, see VAULT_ARCHITECTURE.md §9). */
export function liveVouchDevices(c: Candidate): string[] {
  return [...new Set((c.liveVouch.history ?? []).map((e) => e.deviceKeyB64))]
}

/** A credential is "thin" when it's both newly established AND essentially
 *  uncorroborated (§12.3) — a judgement of *depth*, never of honesty. Farms mint
 *  identities just before they're needed, so thinness is a useful, honest signal
 *  to surface; it is not an accusation (an old credential can still be fraud). */
export function isThinCredential(c: Candidate): boolean {
  const young = c.establishedAt ? new Date(c.establishedAt).getTime() > Date.now() - 14 * 86_400_000 : true
  const relationships = c.vouchesIn?.length ?? 0
  return young && presentSignals(c).length <= 1 && relationships === 0
}

// Shared candidate-derivation helpers. Verification is treated as a positive,
// additive signal here: the score only ever rewards present proofs — it never
// penalizes a candidate for missing one (the product never filters out the
// unverified, it only surfaces the verified).

export function presentSignals(c: Candidate): Signal[] {
  return c.signals.filter((s) => s.present)
}

export function signalCounts(c: Candidate): { present: number; total: number } {
  return { present: presentSignals(c).length, total: c.signals.length }
}

/** Has every *gating* signal on the given ladder rung been met (the rung is fully
 *  *complete*)? Drives the per-rung "Met" badge. Corroborating signals (e.g. LinkedIn)
 *  add confidence but never gate the rung. Tier 3 additionally requires hardware-backed
 *  device custody on each gating proof (iOS Secure Enclave + App Attest), so a web
 *  (WebAuthn passkey) credential caps at Tier 2. A passed Live Vouch satisfies the
 *  Tier-1 `liveness` slot (it *is* a real-time liveness proof), so the rung badge agrees
 *  with the displayed tier. This is the rung-completion predicate `api/lib/tier.mjs
 *  recomputeTier` applies to the rungs *below* the one it's testing; the displayed tier
 *  itself advances leniently (≥1 gating signal on the rung you stand on) — see
 *  recomputeTier. */
export function tierMet(c: Candidate, tier: TrustTier): boolean {
  const rung = c.signals.filter(
    (s) => s.tier === tier && !CORROBORATING_SIGNAL_IDS.has(s.id) && !PAUSED_SIGNAL_IDS.has(s.id),
  )
  if (rung.length === 0) return false
  const liveVouchPresent = c.signals.some((s) => s.id === 'live_vouch' && s.present)
  return rung.every(
    (s) =>
      (s.present && !(tier === 3 && s.custody && s.custody !== 'device')) ||
      (s.id === 'liveness' && liveVouchPresent),
  )
}

// ── Cross-corroboration over the OAuth identity signals (GitHub + LinkedIn) ────
// Independent accounts agreeing on the same human is stronger evidence than any
// one alone. A pure derivation over the candidate's signals; a name mismatch is
// surfaced for a human to review — never an auto-block (legitimate name diffs
// exist). Mirrors the iOS `ProfileCorroboration` (ProfileCorroboration.swift).

const IDENTITY_PROVIDERS: Record<string, string> = { github: 'GitHub', linkedin: 'LinkedIn' }

export type CorroborationVerdict = 'corroborated' | 'single' | 'mismatch'

export interface CorroborationCheck {
  label: string
  detail: string
  state: 'pass' | 'info' | 'fail'
}

export interface ProfileCorroboration {
  verdict: CorroborationVerdict
  summary: string
  checks: CorroborationCheck[]
}

const normalizeName = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining diacritics
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')

const nameTokens = (name: string): string[] => normalizeName(name).split(' ').filter(Boolean)

// Two name tokens are compatible if equal, or one is the initial of the other.
const tokenMatch = (a: string, b: string): boolean =>
  a === b || (a.length === 1 && b.startsWith(a)) || (b.length === 1 && a.startsWith(b))

/** Whether two full names plausibly belong to the same person: first and last
 *  names line up (allowing an initial), middle names/initials may differ or be
 *  absent ("Kyle Johnson" \u2194 "Kyle Paul Johnson"). Mirrors iOS `namesMatch`. */
export function namesMatch(a: string, b: string): boolean {
  const ta = nameTokens(a)
  const tb = nameTokens(b)
  if (ta.length === 0 || tb.length === 0) return false
  if (ta.length === 1 || tb.length === 1) {
    const [single, other] = ta.length === 1 ? [ta[0], tb] : [tb[0], ta]
    return other.some((t) => tokenMatch(t, single))
  }
  return tokenMatch(ta[0], tb[0]) && tokenMatch(ta[ta.length - 1], tb[tb.length - 1])
}

const allNamesMatch = (names: string[]): boolean =>
  names.every((n, i) => names.slice(i + 1).every((m) => namesMatch(n, m)))

/** The vanity slug from a LinkedIn URL (`…/in/<slug>`), or the raw string. */
const linkedinSlug = (url: string): string => {
  const m = url.match(/\/in\/([^/?#]+)/i)
  return m ? m[1] : url
}

/** Whether a listed LinkedIn URL's handle is consistent with a verified name —
 *  first + last name both appear in the slug. Mirrors iOS linkedinSlugMatchesName. */
export function linkedinSlugMatchesName(url: string, name: string): boolean {
  const key = normalizeName(linkedinSlug(url)).replace(/[^a-z0-9]/g, '')
  const parts = nameTokens(name)
  if (parts.length === 0 || key === '') return false
  return key.includes(parts[0]) && key.includes(parts[parts.length - 1])
}

/** Cross-corroboration over the present GitHub/LinkedIn profiles, or `null` when
 *  none is on file (nothing to show). */
export function profileCorroboration(c: Candidate): ProfileCorroboration | null {
  const profiles = c.signals
    .filter((s): s is Signal & { profile: ProfileFacts } => Boolean(s.present && s.profile && IDENTITY_PROVIDERS[s.id]))
    .map((s) => ({ provider: IDENTITY_PROVIDERS[s.id], facts: s.profile }))
  if (profiles.length === 0) return null

  const checks: CorroborationCheck[] = profiles.map((p) => ({
    label: `${p.provider} ownership`,
    detail: 'OAuth-verified — the candidate signed in and controls the account.',
    state: 'pass',
  }))

  const github = profiles.find((p) => p.provider === 'GitHub')
  if (github && github.facts.active !== null) {
    checks.push({
      label: 'GitHub activity',
      detail: github.facts.active ? 'Recent public activity on file.' : 'No recent public activity found.',
      state: github.facts.active ? 'pass' : 'info',
    })
  }

  const named = profiles.filter((p) => normalizeName(p.facts.name) !== '')
  let verdict: CorroborationVerdict
  if (named.length >= 2) {
    const names = named.map((p) => p.facts.name)
    const allAgree = allNamesMatch(names)
    const identical = new Set(names.map(normalizeName)).size === 1
    const display = named.map((p) => `${p.provider}: ${p.facts.name}`).join(' · ')
    const detail = identical
      ? `Same name on every connected profile (${display}).`
      : allAgree
        ? `Names line up across profiles (${display}) — the difference is a middle name or initial, not a different person.`
        : `Names differ across profiles (${display}). Flagged for a human to review — never an auto-block.`
    checks.push({ label: 'Name match across platforms', detail, state: allAgree ? 'pass' : 'fail' })
    verdict = allAgree ? 'corroborated' : 'mismatch'
  } else {
    verdict = 'single'
  }

  // Cross-linked accounts: does the GitHub profile publicly point to a LinkedIn
  // consistent with the same person? Matched on the listed handle vs the verified
  // name (LinkedIn's sign-in returns no public URL to byte-compare). Reuses the
  // `github` profile resolved above for the activity check.
  const declared = github?.facts.declaredLinkedin
  if (declared) {
    const verifiedName = named[0]?.facts.name ?? github!.facts.name
    const linkedinOwned = profiles.some((p) => p.provider === 'LinkedIn')
    const nameOK = linkedinSlugMatchesName(declared, verifiedName)
    if (linkedinOwned && nameOK) {
      checks.push({
        label: 'Cross-linked profiles',
        detail: `GitHub publicly links to LinkedIn (${declared}) and the candidate OAuth-verified a LinkedIn with the same name — the accounts reference each other.`,
        state: 'pass',
      })
    } else if (nameOK) {
      checks.push({
        label: 'Cross-linked profiles',
        detail: `GitHub publicly links to a LinkedIn (${declared}) whose handle matches the verified name.`,
        state: 'pass',
      })
    } else {
      checks.push({
        label: 'Cross-linked profiles',
        detail: `GitHub lists a LinkedIn (${declared}), but its handle doesn't clearly match the name — worth a glance.`,
        state: 'info',
      })
    }
  }

  // Location corroboration: do the locations the profiles list (GitHub), the
  // candidate's stated location, and their phone country agree? Never a block —
  // location is maskable (VPN, travel, "Remote"); a conflict is a review flag.
  const profileLocations = profiles
    .filter((p) => (p.facts.location ?? '').trim() !== '')
    .map((p) => ({ label: p.provider, text: p.facts.location as string }))
  const phoneRisk = c.signals.find((s) => s.id === 'phone')?.risk
  const phoneCountryCode = phoneRisk?.countryCode
  const phoneRegion = phoneRisk?.location?.state // PhoneID line-registration state (e.g. "WA")
  const deviceRegion = c.deviceLocation?.country
    ? { country: c.deviceLocation.country, region: c.deviceLocation.region }
    : undefined
  const locationCheck = locationConsistencyCheck(
    c.location,
    phoneCountryCode,
    profileLocations,
    deviceRegion,
    phoneRegion,
  )
  if (locationCheck) checks.push(locationCheck)

  const summary =
    verdict === 'corroborated'
      ? 'Independent accounts corroborate the same person.'
      : verdict === 'mismatch'
        ? "Connected profiles don't agree on the name — worth a human look."
        : 'One verified profile on file.'

  return { verdict, summary, checks }
}

/** A monotonic "verification strength" used purely for default sort order. */
export function verificationScore(c: Candidate): number {
  const weight: Record<TrustTier, number> = { 1: 1, 2: 2, 3: 4 }
  const fromSignals = presentSignals(c).reduce((sum, s) => sum + weight[s.tier], 0)
  const live = c.state === 'live' ? 3 : 0
  const liveVouch = c.liveVouch.status === 'confirmed' ? 5 : 0
  return fromSignals + live + liveVouch
}
